import { motion } from "framer-motion";
import { Cpu, Gauge, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../lib/images";
import { Reveal } from "./Reveal";

const machines = [
  {
    name: "DMG DMU 80",
    type: "Nuovo investimento · Centro 5 assi",
    desc: "Fresatura simultanea a 5 assi di elevata potenza per componenti di grandi dimensioni e geometrie complesse, con la massima ripetibilità.",
    img: IMAGES.cobi5axis,
    icon: Cpu,
  },
  {
    name: "HURON K2X8 Five",
    type: "Centro di fresatura a 5 assi",
    desc: "Alta velocità e rigidità per lavorazioni di precisione su acciai e leghe speciali, ideale per prototipi e produzione di serie.",
    img: IMAGES.cobiMilling,
    icon: Gauge,
  },
];

const fresatura = [
  "DMG DMU 80 · 5 assi",
  "DMG DMU 75 Monoblock · 5 assi",
  "DMG DMU 50 · 5 assi",
  "DMG CMX 1100 V · 3 assi",
  "Huron K2X8 Five · 5 assi",
  "Huron K3X8 Five · 5 assi (×2)",
  "Huron KX50 · 5 assi",
  "Huron KX15 · 5 assi",
  "Huron K2X8 Five · 5 assi",
  "Huron MX8 · 5 assi",
  "Huron VX12 · 4 assi",
  "Huron VX12 · 3 assi (×3)",
];

const tornitura = [
  "Samsung PL-20 A",
  "Samsung PL-20 MC",
  "Jyoti DX 200",
  "Jyoti DX 350",
  "Mori Seiki NLX 2500-SY 700",
  "Mori Seiki NLX 2500-SY 700 + Robot Arm",
];

const ParkColumn = ({ title, note, items, startIndex }) => (
  <div className="border border-white/10">
    <div className="px-6 md:px-8 py-5 border-b border-white/10 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="w-2 h-2 bg-red-600 rotate-45" />
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-200">{title}</span>
      </div>
      <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">{note}</span>
    </div>
    <div>
      {items.map((m, i) => (
        <div
          key={m}
          data-testid={`park-${startIndex + i}`}
          className="group flex items-center gap-4 px-6 md:px-8 py-4 border-b border-white/10 last:border-b-0 hover:bg-white/[0.03] transition-colors"
        >
          <span className="font-mono text-xs text-red-600 w-6">{String(i + 1).padStart(2, "0")}</span>
          <span className="text-zinc-200">{m}</span>
        </div>
      ))}
    </div>
  </div>
);

export const Machinery = () => (
  <section id="tecnologia" data-testid="machinery" className="border-t border-white/10">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-red-600" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-red-600">Parco Macchine</span>
            </div>
            <h2 className="font-display font-black uppercase text-5xl md:text-7xl tracking-tighter leading-none">
              Tecnologia<br />e precisione
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 leading-relaxed">
            Il reparto fresatura, cuore pulsante della Cobi Meccanica, dispone di 15 frese a controllo
            numerico. Un parco macchine in continua espansione, con i nuovi centri a 5 assi
            DMG DMU 80 e Huron K2X8 Five.
          </p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {machines.map((m, i) => {
          const Icon = m.icon;
          return (
            <Reveal key={m.name} delay={i * 0.12}>
              <motion.div
                whileHover={{ y: -6 }}
                data-testid={`machine-${i}`}
                className="group relative border border-white/10 overflow-hidden min-h-[420px] flex flex-col justify-end"
              >
                <img src={m.img} alt={m.name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                <div className="absolute top-6 left-6 text-red-600"><Icon size={30} strokeWidth={1.4} /></div>
                <ArrowUpRight className="absolute top-6 right-6 text-white/40 group-hover:text-red-600 group-hover:rotate-45 transition-all duration-500" />
                <div className="relative z-10 p-8">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">{m.type}</span>
                  <h3 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-3 mb-4">{m.name}</h3>
                  <p className="text-zinc-300 leading-relaxed max-w-md">{m.desc}</p>
                </div>
              </motion.div>
            </Reveal>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Reveal>
          <ParkColumn title="Reparto Fresatura" note="15 centri CNC" items={fresatura} startIndex={0} />
        </Reveal>
        <Reveal delay={0.1}>
          <ParkColumn title="Reparto Tornitura" note="Tolleranze al micron" items={tornitura} startIndex={20} />
        </Reveal>
      </div>
    </div>
  </section>
);
